import { isRecordingModeNotApplicable } from "@/api";
import { formatDataOrigin } from "@/components/dataset/dataOrigin";
import { formatRecordingMode } from "@/components/filter/datasetFilters";
import { axis, DIFF_EMPTY, type DiffAxis } from "@/components/dataset/diff";
import {
  formatDraftLabels,
  formatDraftTip,
  labelsForKey,
  type DraftConfiguration,
} from "./datasetConfigurationDraft";

/** Dataset 정보·설정·수동 상태의 변경 축과 표시 문자열. */

/**
 * 축 이름. 표시 문구이면서 축의 식별자다.
 *
 * 카드의 `변경됨` 표시는 이 이름으로 "어느 축이 바뀌었나"를 찾는다. 그래서 문구만 고치면 표시가
 * 경고 없이 동작하지 않는다. 리터럴을 여기 모아서, 문구를 바꾸면 호출부에도 반영되게 한다.
 */
export const DIFF_AXIS = {
  name: "이름",
  operator: "담당자",
  dataOrigin: "행동 주체",
  recordingMode: "수집 방식",
  embodiment: "Embodiment",
  tip: "팁",
  labels: "라벨",
} as const;

/**
 * 값이 없는 축의 표시. 공용 대시(—) 대신 낱말로 적는다.
 *
 * 이 요약은 "이전에 무엇이었고 무엇으로 바꿔 달라는가" 를 문장처럼 읽는 자리다. 업로드 직후처럼
 * 이전 값이 모두 비어 있는 경우가 흔하다. 기호만 있으면 값이 없는 것인지 표시 오류인지 구분할
 * 수 없다.
 */
const DIFF_NONE = "없음";

function orNone(value: string): string {
  return value === DIFF_EMPTY ? DIFF_NONE : value;
}

/** 설정 축(embodiment·팁·라벨)의 before/after. */
export function configurationDiff(
  before: DraftConfiguration,
  after: DraftConfiguration,
): readonly DiffAxis[] {
  return [
    axis(DIFF_AXIS.name, before.displayName.trim(), after.displayName.trim()),
    {
      ...axis(
        DIFF_AXIS.operator,
        before.operatorMember?.display_name ?? DIFF_NONE,
        after.operatorMember?.display_name ?? DIFF_NONE,
      ),
      changed: before.operatorMember?.uuid !== after.operatorMember?.uuid,
    },
    axis(
      DIFF_AXIS.dataOrigin,
      formatDataOrigin(before.dataOrigin) ?? DIFF_NONE,
      formatDataOrigin(after.dataOrigin) ?? DIFF_NONE,
    ),
    axis(
      DIFF_AXIS.recordingMode,
      formatDraftRecordingMode(before),
      formatDraftRecordingMode(after),
    ),
    axis(
      DIFF_AXIS.embodiment,
      before.embodiment?.name ?? DIFF_NONE,
      after.embodiment?.name ?? DIFF_NONE,
    ),
    axis(DIFF_AXIS.tip, orNone(formatDraftTip(before)), orNone(formatDraftTip(after))),
    axis(DIFF_AXIS.labels, orNone(formatDraftLabels(before)), orNone(formatDraftLabels(after))),
  ];
}

/** 행동 주체와 독립적인 수집 방식 및 자동/수동 상태 표시. */
export function formatDraftRecordingMode(draft: DraftConfiguration): string {
  const external = labelsForKey(draft, "source").some((label) => label.value === "open_source");
  const mode =
    !draft.recordingMode.modified_manually && external
      ? { ...draft.recordingMode, value: null }
      : draft.recordingMode;
  const value = isRecordingModeNotApplicable(mode, external)
    ? "해당 없음"
    : (formatRecordingMode(mode.value) ?? "미판정");
  return `${value} (${mode.modified_manually ? "수동 지정" : "자동 판정"})`;
}
